import { useState } from 'react';
import { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Search, ShoppingCart, User, Heart, ChevronDown } from 'lucide-react';
import { NAV_ITEMS, ROUTE_PATHS } from '@/lib/index';
import { useCart } from '@/hooks/useCart';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';

export function Layout({ children }: { children: ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const totalCount = useCart((state) => state.getTotalCount());

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header: two-row layout matching screenshot */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100">
        {/* Top bar: logo left, icons right */}
        <div className="w-full px-6 lg:px-10">
          <div className="flex items-center justify-between h-14">
            {/* Left: menu icon + logo */}
            <div className="flex items-center gap-4">
              {/* Small square menu button visible on all sizes */}
              <button className="w-8 h-8 bg-black flex items-center justify-center lg:hidden">
                <Menu className="w-4 h-4 text-white" />
              </button>

              <Link
                to={ROUTE_PATHS.HOME}
                className="text-2xl font-black tracking-tight leading-none"
              >
                <span className="text-black">DO</span>
                <span className="text-red-600">!</span>
                <span className="text-black">NK</span>
              </Link>
            </div>

            {/* Right icons */}
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="w-9 h-9 rounded-full">
                <Search className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="w-9 h-9 rounded-full hidden md:flex">
                <User className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="w-9 h-9 rounded-full hidden md:flex">
                <Heart className="h-4 w-4" />
              </Button>
              <Link to="/cart">
                <Button variant="ghost" size="icon" className="w-9 h-9 rounded-full relative">
                  <ShoppingCart className="h-4 w-4" />
                  {totalCount > 0 && (
                    <Badge
                      variant="default"
                      className="absolute -top-0.5 -right-0.5 h-4 w-4 flex items-center justify-center p-0 text-[10px] rounded-full"
                    >
                      {totalCount}
                    </Badge>
                  )}
                </Button>
              </Link>

              {/* Mobile hamburger */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden w-9 h-9 rounded-full">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-5 border-b border-gray-100">
                      <span className="text-2xl font-black">
                        <span className="text-black">DO</span>
                        <span className="text-red-600">!</span>
                        <span className="text-black">NK</span>
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                        <X className="h-5 w-5" />
                      </Button>
                    </div>
                    <nav className="flex flex-col p-4 gap-1">
                      {NAV_ITEMS.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={({ isActive }) =>
                            `text-sm font-medium py-2.5 px-3 transition-colors ${
                              isActive
                                ? 'text-black bg-gray-50'
                                : 'text-gray-500 hover:text-black hover:bg-gray-50'
                            }`
                          }
                        >
                          {item.label}
                        </NavLink>
                      ))}
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Navigation row — desktop only */}
        <div className="hidden lg:block border-t border-gray-100">
          <div className="w-full px-10">
            <nav className="flex items-center gap-0 h-10">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `text-xs font-medium px-3 h-full flex items-center gap-1 border-b-2 transition-colors whitespace-nowrap ${
                      isActive
                        ? 'text-black border-black'
                        : 'text-gray-500 border-transparent hover:text-black hover:border-gray-300'
                    }`
                  }
                >
                  {item.label}
                  {item.label === 'Brands' && <ChevronDown className="w-3 h-3" />}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Account for two-row header height */}
      <main className="flex-1 pt-[96px] lg:pt-[96px]">
        {children}
      </main>

      <footer className="bg-gray-50 border-t border-gray-100 mt-0">
        <div className="container mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-black mb-4">
                <span className="text-black">DO</span>
                <span className="text-red-600">!</span>
                <span className="text-black">NK</span>
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Professional tattoo supplies for artists who demand excellence.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-black">Shop</h4>
              <ul className="space-y-2">
                {[
                  { label: 'Cartridge Needles', path: '/products/cartridges' },
                  { label: 'Tattoo Ink', path: '/products/ink' },
                  { label: 'Stencils', path: '/products/stencils' },
                  { label: 'Aftercare', path: '/products/aftercare' },
                ].map((l) => (
                  <li key={l.path}>
                    <Link to={l.path} className="text-xs text-gray-500 hover:text-black transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-black">Support</h4>
              <ul className="space-y-2">
                {['Contact Us', 'Shipping Info', 'Returns', 'FAQ'].map((l) => (
                  <li key={l}>
                    <a href="#" className="text-xs text-gray-500 hover:text-black transition-colors">{l}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-4 text-black">Company</h4>
              <ul className="space-y-2">
                {['About Us', 'News', 'Privacy Policy', 'Terms of Service'].map((l) => (
                  <li key={l}>
                    <a href="#" className="text-xs text-gray-500 hover:text-black transition-colors">{l}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">© 2026 DOINK. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
